import React, { FC, useMemo } from 'react';
import { ReferenceMemberParameter, isReferenceMemberParameter } from '@modules/client';
import '@rbx/ui';
import {
  TypeAliasDatum,
  TypeAliasDatumItem,
  isHybridTypeAliasDatum,
  isMultiOrTypesCompoundTypeAliasDatum,
  isTypeAliasDatumItem,
} from '../../types';
import ReferenceMemberTypeItem from './ReferenceMemberTypeItem';
import { useReferenceTypesData } from './ReferenceTypesDataProvider';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';

interface ReferenceMemberTypeProps {
  parameter: ReferenceMemberParameter | TypeAliasDatum;
  isOptional: boolean; // type is optional (ends with '?' indicator)
  engineRevampEnabled?: boolean;
}

const WrappedTypeItem = ({
  typeAliasDatumItem,
  isOptional, // type is optional (ends with '?' indicator)
  engineRevampEnabled,
}: {
  typeAliasDatumItem: TypeAliasDatumItem;
  isOptional: boolean;
  engineRevampEnabled?: boolean;
}) => {
  if (!typeAliasDatumItem)
    throw new Error(
      `Type alias not found for typeAliasDatumItem: ${JSON.stringify(typeAliasDatumItem)}`,
    );
  const defaultStyles = useReferenceSummaryMemberTableStyles();
  const v2Styles = useReferenceSummaryMemberTableStylesV2();
  const { classes, cx } = engineRevampEnabled ? v2Styles : defaultStyles;

  const url = useMemo(
    () => (typeAliasDatumItem.path ? `${typeAliasDatumItem.path}` : undefined),
    [typeAliasDatumItem],
  );
  return (
    // note: psuedo selector style (:before) does not work in makeExpStyle
    // => use parent selector > '& :before' instead
    <span className={cx(classes.parameterType, classes.dashedTypeWrapper)}>
      <ReferenceMemberTypeItem
        url={url}
        tooltipProps={typeAliasDatumItem?.tooltipProps}
        label={typeAliasDatumItem.canonicalLabel}
        engineRevampEnabled={engineRevampEnabled}
      />
      {isOptional && <span>?</span>}
    </span>
  );
};

const ReferenceMemberType: FC<React.PropsWithChildren<ReferenceMemberTypeProps>> = ({
  parameter,
  isOptional,
  engineRevampEnabled = false,
}) => {
  const { getTypeAliasDatum, getTypeAliasDatumItem } = useReferenceTypesData();
  let aliasDatum: TypeAliasDatum;
  if (isReferenceMemberParameter(parameter)) {
    // parameter.type is some yaml string 'Array<BundleType>' or 'Material' etc
    if (!parameter.type)
      throw new Error(`Parameter type is not defined on parameter ${JSON.stringify(parameter)}`);
    aliasDatum = getTypeAliasDatum(parameter.type);
  } else {
    aliasDatum = parameter;
  }

  if (isMultiOrTypesCompoundTypeAliasDatum(aliasDatum)) {
    // spec: display as 'PossibleType1 | PossibleType2 | ...'
    const { multipleOrTypes } = aliasDatum;
    return (
      <span>
        {multipleOrTypes.map((typeAliasDatumItem, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i.toString()}>
            <WrappedTypeItem
              typeAliasDatumItem={typeAliasDatumItem}
              isOptional={isOptional}
              engineRevampEnabled={engineRevampEnabled}
            />
            {i !== multipleOrTypes.length - 1 && <span> | </span>}
          </React.Fragment>
        ))}
      </span>
    );
  }
  if (isHybridTypeAliasDatum(aliasDatum)) {
    // spec: display as 'OuterType<InnerType, InnerType2,...>'
    const { outerType, innerTypes } = aliasDatum;
    return (
      <span>
        <WrappedTypeItem
          typeAliasDatumItem={outerType}
          isOptional={isOptional}
          engineRevampEnabled={engineRevampEnabled}
        />
        <span>&lt;</span>
        {innerTypes.map((typeAliasDatumItem, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i.toString()}>
            <WrappedTypeItem
              typeAliasDatumItem={typeAliasDatumItem}
              isOptional={isOptional}
              engineRevampEnabled={engineRevampEnabled}
            />
            {i !== innerTypes.length - 1 && <span>, </span>}
          </React.Fragment>
        ))}
        <span>&gt;</span>
      </span>
    );
  }

  // single type case
  let aliasDatumItem: TypeAliasDatumItem | undefined;
  if (isReferenceMemberParameter(parameter)) {
    aliasDatumItem = getTypeAliasDatumItem(parameter.type);
  } else if (isTypeAliasDatumItem(parameter)) {
    aliasDatumItem = parameter;
  }
  if (!aliasDatumItem)
    throw new Error(
      `Type alias not found for typeAliasDatumItem: ${JSON.stringify(aliasDatumItem)}`,
    );
  return (
    <WrappedTypeItem
      typeAliasDatumItem={aliasDatumItem}
      isOptional={isOptional}
      engineRevampEnabled={engineRevampEnabled}
    />
  );
};

export default ReferenceMemberType;
